import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { abacGuard } from '@saraa/permissions/abacMiddleware.js';
import { Organizations } from '@saraa/permissions/policies/index.js';
import { PAGE_CHECK } from '@saraa/permissions/policyHelper.js';
import { Organization } from '../entities/Organization.js';
import { loadOrganizationTarget } from '../server/abacTargets.js';
import { resolveCreateOrganization } from '../server/resolvers/resolveCreateOrganization.js';
import {
  resolveDeleteOrganization,
  resolveSetOrganizationArchived,
} from '../server/resolvers/resolveDeleteOrganization.js';
import {
  resolveGetMyOrganizations,
  resolveGetOrganizationById,
  resolveSubscribeOrganizations,
} from '../server/resolvers/resolveReadOrganizations.js';
import { resolveUpdateOrganization } from '../server/resolvers/resolveUpdateOrganization.js';

/**
 * Every way an organization can be read or written.
 *
 * The generic entity mutations are closed for this class, so these are it. Each
 * one carries its own `abacGuard`, which is the whole point: the permission a
 * caller needs is stated on the endpoint that needs it, and adding an endpoint
 * without one is a visible omission rather than a forgotten row in a table
 * somewhere else.
 *
 * The `modify` and `delete` guards resolve their target from the database rather
 * than from the request — for this scope the checks run against the row's own
 * `_id`, so a target taken from the caller would be the caller answering their
 * own question.
 */

const createOrganization = registerMutation({
  operationName: 'createOrganization',
  type: () => ID,
  params: [{ name: 'organization', type: () => Organization }],
  // Bringing an organization into existence is platform-admin work: there is no
  // organization to be an admin *of* yet, so no check but `isAdmin` can pass.
  middlewares: [abacGuard(Organizations, 'create')],
  resolve: resolveCreateOrganization,
});

const updateOrganization = registerMutation({
  operationName: 'updateOrganization',
  type: () => Boolean,
  params: [{ name: 'organization', type: () => Organization }],
  middlewares: [
    abacGuard(Organizations, 'modify', (args: { organization: Organization }) =>
      loadOrganizationTarget(args.organization?._id),
    ),
  ],
  resolve: resolveUpdateOrganization,
});

const deleteOrganization = registerMutation({
  operationName: 'deleteOrganization',
  type: () => Boolean,
  params: [{ name: 'organizationId', type: () => ID }],
  middlewares: [
    abacGuard(Organizations, 'delete', (args: { organizationId: string }) =>
      loadOrganizationTarget(args.organizationId),
    ),
  ],
  resolve: resolveDeleteOrganization,
});

const setOrganizationArchived = registerMutation({
  operationName: 'setOrganizationArchived',
  type: () => Boolean,
  params: [
    { name: 'organizationId', type: () => ID },
    { name: 'archived', type: () => Boolean },
  ],
  // Archiving takes an organization out of use, so it answers to the same
  // permission as deleting it.
  middlewares: [
    abacGuard(Organizations, 'delete', (args: { organizationId: string }) =>
      loadOrganizationTarget(args.organizationId),
    ),
  ],
  resolve: resolveSetOrganizationArchived,
});

const subscribeOrganizations = registerSubscription({
  operationName: 'subscribeOrganizations',
  type: () => [Organization],
  // `(skip, limit, filter)` is the shape the data-view calls a list operation
  // with; deviating from it means the ERM list cannot use this.
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  /**
   * A list has no single row to decide against, so the guard answers the coarse
   * question — may this user look at organizations at all — and the resolver
   * makes the per-row decision through its scope selector.
   */
  middlewares: [abacGuard(Organizations, 'view', () => PAGE_CHECK)],
  resolve: resolveSubscribeOrganizations,
});

const getOrganizationById = registerQuery({
  operationName: 'getOrganizationById',
  type: () => Organization,
  params: [{ name: 'organizationId', type: () => ID }],
  middlewares: [
    abacGuard(Organizations, 'view', (args: { organizationId: string }) => loadOrganizationTarget(args.organizationId)),
  ],
  resolve: resolveGetOrganizationById,
});

const getMyOrganizations = registerQuery({
  operationName: 'getMyOrganizations',
  type: () => [Organization],
  params: [],
  middlewares: [abacGuard(Organizations, 'view', () => PAGE_CHECK)],
  resolve: resolveGetMyOrganizations,
});

export const OrganizationsAPI = {
  create: createOrganization,
  update: updateOrganization,
  delete: deleteOrganization,
  setArchived: setOrganizationArchived,
  subscribe: subscribeOrganizations,
  getById: getOrganizationById,
  getMine: getMyOrganizations,
};
